import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function BusinessSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.15 }
    );

    const els = sectionRef.current?.querySelectorAll(".animate-in");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const businesses = [
    {
      id: "chip-trade",
      nameKey: "business.chipTrade.name",
      nameEnKey: "business.chipTrade.nameEn",
      descKey: "business.chipTrade.desc",
      specKey: "business.chipTrade.spec",
      img: "https://readdy.ai/api/search-image?query=Global%20semiconductor%20chip%20trade%20logistics%2C%20stacked%20NAND%20flash%20and%20DRAM%20memory%20chips%20on%20dark%20matte%20surface%20with%20subtle%20warm%20amber%20lighting%2C%20international%20trade%20atmosphere%2C%20premium%20electronic%20components%20photography%20with%20shallow%20depth%20of%20field%2C%20professional%20studio%20lighting%20emphasizing%20metallic%20textures%20and%20circuitry%20details%2C%20sophisticated%20corporate%20aesthetic&width=600&height=450&seq=ax-chip-trade-1&orientation=landscape",
    },
    {
      id: "consumer-brand",
      nameKey: "business.consumerBrand.name",
      nameEnKey: "business.consumerBrand.nameEn",
      descKey: "business.consumerBrand.desc",
      specKey: "business.consumerBrand.spec",
      img: "https://readdy.ai/api/search-image?query=Premium%20consumer%20storage%20devices%20collection%2C%20sleek%20portable%20SSD%20drive%20and%20high-speed%20SD%20memory%20card%20arranged%20on%20clean%20minimal%20surface%20with%20soft%20diffused%20lighting%2C%20Tesla-inspired%20industrial%20design%20with%20brushed%20aluminum%20finish%2C%20minimalist%20product%20photography%20with%20warm%20neutral%20tones%2C%20elegant%20tech%20accessories%20layout%2C%20professional%20studio%20shot%20with%20subtle%20shadows&width=600&height=450&seq=ax-consumer-brand-1&orientation=landscape",
    },
    {
      id: "ai-nas",
      nameKey: "business.aiNAS.name",
      nameEnKey: "business.aiNAS.nameEn",
      descKey: "business.aiNAS.desc",
      specKey: "business.aiNAS.spec",
      img: "https://readdy.ai/api/search-image?query=Advanced%20AI-powered%20network%20attached%20storage%20system%2C%20futuristic%20NAS%20device%20with%20holographic%20interface%20glow%2C%20dark%20sophisticated%20environment%20with%20subtle%20blue%20and%20warm%20accent%20lighting%2C%20intelligent%20data%20center%20aesthetic%20with%20neural%20network%20visualization%20overlay%2C%20premium%20tech%20product%20photography%2C%20enterprise-grade%20hardware%20with%20elegant%20design%2C%20soft%20atmospheric%20lighting%20emphasizing%20smart%20technology%20features&width=600&height=450&seq=ax-ai-nas-1&orientation=landscape",
    },
  ];

  return (
    <section
      id="business"
      ref={sectionRef}
      className="bg-ax-light py-20 md:py-28"
    >
      <div className="px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="mb-16 animate-in opacity-0 translate-y-6 transition-all duration-700">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-ax-dark font-bold">
            <span className="block">{t("business.title")}</span>
            <span className="block text-ax-red mt-1">{t("business.subtitle")}</span>
          </h2>
        </div>

        {/* Business Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {businesses.map((b, i) => (
            <div
              key={b.id}
              className="animate-in opacity-0 translate-y-6 transition-all duration-700 bg-white rounded-2xl overflow-hidden border border-ax-silver-light group cursor-pointer"
              style={{ transitionDelay: `${200 + i * 200}ms` }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={b.img}
                  alt={t(b.nameKey)}
                  className="w-full h-[240px] md:h-[280px] object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm px-4 py-2">
                  <p className="text-xs font-medium text-ax-dark/60">
                    {t(b.nameEnKey)}
                  </p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-lg font-bold text-ax-dark">
                    {t(b.nameKey)}
                  </h3>
                  <span className="text-xs text-ax-red font-medium bg-ax-red/5 px-2 py-1 rounded">
                    {t(b.specKey)}
                  </span>
                </div>
                <p className="text-sm text-ax-dark/50 leading-relaxed">
                  {t(b.descKey)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}